import React, { useState, useEffect, useCallback } from 'react';
import { History, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ConfigHistoryEntry {
  id: number;
  device_id: string;
  received_at: string;
  created_at: string;
  trigger_type: string;
  message_type: string;
  report_interval: number | null;
  report_interval_idle: number | null;
  server_ip: string | null;
  server_port: number | null;
  firmware_version: string | null;
  iccid: string | null;
  raw_config: any;
  f0_params: any;
}

interface DeviceConfigHistoryProps {
  deviceId?: string | null;
  className?: string;
}

const DeviceConfigHistory: React.FC<DeviceConfigHistoryProps> = ({ deviceId, className = '' }) => {
  const [history, setHistory] = useState<ConfigHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEnabled, setFilterEnabled] = useState<'all' | 'enabled' | 'disabled'>('all');
  const pageSize = 20;

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let query = supabase
        .from('device_config_history')
        .select('*')
        .order('received_at', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (deviceId) {
        query = query.eq('device_id', deviceId);
      }

      if (searchTerm) {
        query = query.ilike('device_id', `%${searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      setHistory(data || []);
      setHasMore((data?.length || 0) === pageSize);
    } catch (err) {
      console.error('Error loading config history:', err);
      setError('Failed to load configuration history');
    } finally {
      setLoading(false);
    }
  }, [deviceId, page, searchTerm]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatInterval = (seconds: number | null | undefined) => {
    if (!seconds && seconds !== 0) return '-';
    if (seconds === 0) return '0s';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  };

  const getParameterValue = (entry: ConfigHistoryEntry, primaryKey: string, f0xxKey?: string) => {
    let value = null;
    
    // Parse raw_config if it's a string
    let rawConfig = entry.raw_config;
    if (typeof rawConfig === 'string') {
      try {
        rawConfig = JSON.parse(rawConfig);
      } catch (e) {
        console.error('Failed to parse raw_config:', e);
        rawConfig = null;
      }
    }
    
    // Check raw_config first
    if (rawConfig) {
      value = rawConfig[primaryKey];
      
      // Check F0XX parameters if no extracted value found
      if ((value === null || value === undefined) && f0xxKey && rawConfig.f0_params) {
        const f0Param = rawConfig.f0_params[f0xxKey];
        if (f0Param) {
          value = f0Param.parsed !== undefined ? f0Param.parsed : f0Param.value;
        }
      }
    }
    
    // Parse f0_params if it's a string
    let f0Params = entry.f0_params;
    if (typeof f0Params === 'string') {
      try {
        f0Params = JSON.parse(f0Params);
      } catch (e) {
        console.error('Failed to parse f0_params:', e);
        f0Params = null;
      }
    }
    
    // Check f0_params at root level
    if ((value === null || value === undefined) && f0xxKey && f0Params) {
      const f0Param = f0Params[f0xxKey];
      if (f0Param) {
        value = f0Param.parsed !== undefined ? f0Param.parsed : f0Param.value;
      }
    }
    
    return value;
  };

  const getSampleInterval = (entry: ConfigHistoryEntry) => {
    // Sample interval is typically F01E
    const value = getParameterValue(entry, 'sample_interval', 'F01E');
    return formatInterval(value);
  };

  const getReportingInterval = (entry: ConfigHistoryEntry) => {
    // Reporting interval (idle) from multiple possible sources
    const value = entry.report_interval_idle || 
                  entry.report_interval || 
                  getParameterValue(entry, 'report_interval_idle', 'F02F') ||
                  getParameterValue(entry, 'report_interval', 'F01E');
    return formatInterval(value);
  };

  const getMovementInterval = (entry: ConfigHistoryEntry) => {
    // Movement report interval from F02E
    const value = getParameterValue(entry, 'movement_report_interval', 'F02E');
    return formatInterval(value);
  };

  const getGSensorStatus = (entry: ConfigHistoryEntry) => {
    const enabled = getParameterValue(entry, 'gsensor_enable', 'F019');
    if (enabled === null || enabled === undefined) return '-';
    return enabled === 1 || enabled === true ? 'ON' : 'OFF';
  };

  const getGSensorThreshold = (entry: ConfigHistoryEntry) => {
    const threshold = getParameterValue(entry, 'gsensor_threshold', 'F01A') ||
                     getParameterValue(entry, 'gsensor_sensitivity', 'F01A');
    return threshold !== null && threshold !== undefined ? threshold : '-';
  };

  const getTempThresholds = (entry: ConfigHistoryEntry) => {
    const low = getParameterValue(entry, 'temp_threshold_low', 'F025');
    const high = getParameterValue(entry, 'temp_threshold_high', 'F024');
    
    if ((low === null || low === undefined) && (high === null || high === undefined)) {
      return '-';
    }
    
    const lowStr = low !== null && low !== undefined ? `${low}°` : '-';
    const highStr = high !== null && high !== undefined ? `${high}°` : '-';
    
    return `${lowStr} / ${highStr}`;
  };

  const getHumidityThresholds = (entry: ConfigHistoryEntry) => {
    const low = getParameterValue(entry, 'humidity_threshold_low', 'F027');
    const high = getParameterValue(entry, 'humidity_threshold_high', 'F026');
    
    if ((low === null || low === undefined) && (high === null || high === undefined)) {
      return '-';
    }
    
    const lowStr = low !== null && low !== undefined ? `${low}%` : '-';
    const highStr = high !== null && high !== undefined ? `${high}%` : '-';
    
    return `${lowStr} / ${highStr}`;
  };

  const getGeofenceStatus = (entry: ConfigHistoryEntry) => {
    const enabled = getParameterValue(entry, 'geofence_enable', 'F00F');
    if (enabled === null || enabled === undefined) return '-';
    return enabled === 1 || enabled === true ? 'ON' : 'OFF';
  };

  const filteredHistory = history.filter(entry => {
    if (filterEnabled === 'enabled') {
      const gsensor = getGSensorStatus(entry);
      const geofence = getGeofenceStatus(entry);
      if (gsensor !== 'ON' && geofence !== 'ON') return false;
    } else if (filterEnabled === 'disabled') {
      const gsensor = getGSensorStatus(entry);
      const geofence = getGeofenceStatus(entry);
      if (gsensor !== 'OFF' && geofence !== 'OFF') return false;
    }
    return true;
  });

  if (loading && page === 0) {
    return (
      <div className={`bg-white rounded-xl p-6 shadow-sm border border-gray-200 ${className}`}>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-traqpack-yellow border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-xl p-6 shadow-sm border border-gray-200 ${className}`}>
        <div className="text-center text-red-600">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <History className="w-5 h-5 text-traqpack-yellow" />
            <h2 className="text-xl font-bold text-gray-900">Device Configuration History</h2>
          </div>
          {deviceId && (
            <span className="text-sm text-gray-500">
              Device: {deviceId}
            </span>
          )}
        </div>

        {/* Search and Filter Controls */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by IMEI..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-traqpack-yellow focus:border-transparent outline-none"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <select
              value={filterEnabled}
              onChange={(e) => setFilterEnabled(e.target.value as 'all' | 'enabled' | 'disabled')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-traqpack-yellow focus:border-transparent outline-none text-sm"
            >
              <option value="all">All Configurations</option>
              <option value="enabled">Sensors Enabled</option>
              <option value="disabled">Sensors Disabled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Timestamp
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                IMEI
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sample<br/>Interval
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Report<br/>Interval
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Movement<br/>Interval
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                G-Sensor
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Threshold
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Temp<br/>(L/H)
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Humidity<br/>(L/H)
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Geofence
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Trigger
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredHistory.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-4 py-12 text-center text-gray-500">
                  <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p>No configuration history found</p>
                </td>
              </tr>
            ) : (
              filteredHistory.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {formatDate(entry.received_at || entry.created_at)}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900 whitespace-nowrap">
                    {entry.device_id}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-700">
                    {getSampleInterval(entry)}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-700">
                    {getReportingInterval(entry)}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-700">
                    {getMovementInterval(entry)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      getGSensorStatus(entry) === 'ON' 
                        ? 'bg-green-100 text-green-800' 
                        : getGSensorStatus(entry) === 'OFF'
                        ? 'bg-gray-100 text-gray-600'
                        : 'text-gray-400'
                    }`}>
                      {getGSensorStatus(entry)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-700">
                    {getGSensorThreshold(entry)}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-700 whitespace-nowrap">
                    {getTempThresholds(entry)}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-700 whitespace-nowrap">
                    {getHumidityThresholds(entry)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      getGeofenceStatus(entry) === 'ON' 
                        ? 'bg-blue-100 text-blue-800' 
                        : getGeofenceStatus(entry) === 'OFF'
                        ? 'bg-gray-100 text-gray-600'
                        : 'text-gray-400'
                    }`}>
                      {getGeofenceStatus(entry)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-center text-gray-600">
                    {entry.trigger_type?.replace('_', ' ')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(hasMore || page > 0) && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {filteredHistory.length} entries
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="p-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="px-3 py-2 text-sm text-gray-700">
              Page {page + 1}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={!hasMore}
              className="p-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceConfigHistory;